import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { onboardingAPI } from '../services/api';
import {
  ArrowLeft, Loader2, Check, Plus, Trash2, Save, Upload,
  Building2, MapPin, BedDouble, Sparkles, Landmark, CheckCircle2, Lock
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

// iter113 — CRM parity: match exactly the 14 options in CRM Master Form.
const PROPERTY_TYPES = [
  'Apartment', 'Bed & Breakfast', 'Boutique Hotel', 'Camp', 'Cottage',
  'Farmhouse', 'Guest House', 'Home Stay', 'Hostel', 'Hotel',
  'Lodge', 'Resort', 'Service Apartment', 'Villa',
];
const ROOM_TYPE_OPTIONS = ['Standard', 'Deluxe', 'Super Deluxe', 'Suite', 'Executive', 'Premium', 'Family', 'Dormitory', 'Cottage'];
const AC_OPTIONS = ['AC', 'Non-AC'];
const SIZE_UNITS = ['sq ft', 'sq m'];

const ROOM_AMENITIES = ['TV', 'Fan', 'Cooler', 'WiFi', 'Cupboard', 'Sofa', 'Bathroom', 'Jacuzzi', 'Bathtub', 'Toiletries', 'Mirror'];
const PROPERTY_POLICIES = ['Couple Friendly', 'Unmarried Couples Allowed', 'Outside Food Allowed', 'Non-Veg Food Allowed', 'Alcohol Allowed', 'Smoking Allowed Inside Room', 'Local ID Allowed', 'Foreigners Allowed', 'Pet Allowed'];
const CHARGEABLE_FACILITIES = ['Parking', 'Room Heater', 'Breakfast', 'Swimming Pool'];
const OTHER_FACILITIES = ['Security Camera', 'Luggage Storage', 'Outdoor Dining Area', 'Restaurant', 'Kitchen', '24x7 Reception', 'Room Service', 'Power Backup', 'Banquet Hall', 'Conference Room'];

// Conditional sub-options that appear once a parent amenity/facility is selected.
// Each parent maps to one or more mutually-exclusive option groups (CRM parity).
const CONDITIONAL_AMENITIES = {
  'Bathroom': [{ key: 'bathroom_type', label: 'Bathroom Type', options: ['Private', 'Shared'] }],
  'Pet Allowed': [{ key: 'cost', label: 'Pricing', options: ['Free', 'Paid'] }],
  'Parking': [{ key: 'cost', label: 'Pricing', options: ['Free', 'Paid'] }],
  'Room Heater': [{ key: 'cost', label: 'Pricing', options: ['Free', 'Paid'] }],
  'Breakfast': [{ key: 'cost', label: 'Pricing', options: ['Free', 'Paid'] }],
  'Swimming Pool': [
    { key: 'cost', label: 'Pricing', options: ['Free', 'Paid'] },
    { key: 'location', label: 'Location', options: ['Indoor', 'Outdoor'] },
  ],
};

const STEPS = [
  { id: 1, label: 'Property', icon: Building2 },
  { id: 2, label: 'Location', icon: MapPin },
  { id: 3, label: 'Rooms', icon: BedDouble },
  { id: 4, label: 'Amenities', icon: Sparkles },
  { id: 5, label: 'Banking', icon: Landmark },
];

const emptyRoom = () => ({ room_type: '', ac_type: '', no_of_rooms: '', room_size: '', size_unit: 'sq ft', base_price: '', weekend_price: '', extra_adult_price: '', extra_bed_price: '', no_of_adults: '', no_of_children: '', amenities: {} });

const inputCls = 'w-full px-3 py-2.5 bg-gray-50 rounded-lg border border-gray-200 text-gray-900 text-sm focus:border-[#1B2A7B] focus:ring-1 focus:ring-emerald transition-colors disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed';
const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

// ---- Module-level field components (defined OUTSIDE render to preserve input focus) ----
const Field = ({ label, value, onChange, type = 'text', required, placeholder, testId, readOnly, error }) => (
  <div>
    <label className={labelCls}>{label} {required && <span className="text-red-500">*</span>}</label>
    <input
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder || label}
      disabled={readOnly}
      className={`${inputCls} ${error ? 'border-red-400 focus:border-red-500 focus:ring-red-200' : ''}`}
      data-testid={testId}
    />
    {error && (
      <p className="text-[11px] text-red-500 mt-1" data-testid={testId ? `${testId}-error` : undefined}>
        {error}
      </p>
    )}
  </div>
);

const CheckGrid = ({ title, options, selected, onToggle, testIdPrefix, readOnly, conditional, amenityOptions, onOptionChange }) => {
  const sel = selected || [];
  return (
    <div>
      <p className="text-sm font-semibold text-gray-700 mb-2">{title}</p>
      <div className="grid grid-cols-2 gap-2">
        {options.map((opt) => {
          const active = sel.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              disabled={readOnly}
              onClick={() => onToggle(opt)}
              data-testid={`${testIdPrefix}-${opt.replace(/\s+/g, '-').toLowerCase()}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left transition-colors disabled:cursor-not-allowed ${active ? 'bg-[#EEF1FC] border-[#1B2A7B] text-[#1B2A7B]' : 'bg-white border-gray-200 text-gray-600'}`}
            >
              <span className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${active ? 'bg-[#1B2A7B] text-white' : 'border border-gray-300'}`}>
                {active && <Check className="w-3 h-3" />}
              </span>
              {opt}
            </button>
          );
        })}
      </div>

      {/* Conditional sub-options for selected amenities (CRM parity) */}
      {conditional && options.filter((opt) => sel.includes(opt) && conditional[opt]).map((opt) => {
        const optMeta = amenityOptions[opt] || {};
        return (
          <div key={`cond-${opt}`} className="mt-2 ml-1 pl-3 border-l-2 border-[#1B2A7B]/40 space-y-2 py-1" data-testid={`${testIdPrefix}-${opt.replace(/\s+/g, '-').toLowerCase()}-options`}>
            <p className="text-xs font-medium text-gray-500">{opt} details</p>
            {conditional[opt].map((group) => (
              <div key={group.key} className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] text-gray-400 w-16">{group.label}</span>
                  {group.options.map((o) => {
                    const chosen = optMeta[group.key] === o;
                    return (
                      <button
                        key={o}
                        type="button"
                        disabled={readOnly}
                        onClick={() => onOptionChange(opt, group.key, o)}
                        data-testid={`${testIdPrefix}-${opt.replace(/\s+/g, '-').toLowerCase()}-${group.key}-${o.toLowerCase()}`}
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors disabled:cursor-not-allowed ${chosen ? 'bg-[#1B2A7B] text-white border-[#1B2A7B]' : 'bg-white border-gray-200 text-gray-600'}`}
                      >
                        {o}
                      </button>
                    );
                  })}
                </div>
                {/* iter84 — When cost = Paid, show the mandatory price input */}
                {group.key === 'cost' && optMeta.cost === 'Paid' && (
                  <div className="flex items-center gap-2 pl-[72px]">
                    <span className="text-[11px] text-gray-400">Price</span>
                    <div className="flex items-center bg-white border border-gray-200 rounded-lg px-2 py-1 focus-within:border-[#1B2A7B]">
                      <span className="text-xs text-gray-500 mr-1">₹</span>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        disabled={readOnly}
                        value={optMeta.price ?? ''}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const val = raw === '' ? '' : Math.max(0, parseInt(raw, 10) || 0);
                          onOptionChange(opt, 'price', val);
                        }}
                        placeholder="0"
                        className="w-20 text-sm bg-transparent border-0 outline-none disabled:text-gray-500 disabled:cursor-not-allowed"
                        data-testid={`${testIdPrefix}-${opt.replace(/\s+/g, '-').toLowerCase()}-price`}
                      />
                    </div>
                    {!optMeta.price || optMeta.price <= 0 ? (
                      <span className="text-[11px] text-red-500" data-testid={`${testIdPrefix}-${opt.replace(/\s+/g, '-').toLowerCase()}-price-error`}>Required</span>
                    ) : null}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

// iter84 — Per-room amenities block. Each room gets its OWN amenity selection
// (stored on room.amenities as a dict). Backward-compat: if the doc is legacy
// and has only the property-wide `room_amenities` array, the first room block
// shows that legacy set for read-only review only.
const RoomAmenitiesBlock = ({ room, roomIndex, onChange, readOnly }) => {
  const amenities = room.amenities || {};
  // iter151 — Only surface amenities that are actually selected. Previously we
  // used Object.keys() blindly, which would tick a checkbox for entries like
  // {value: "No"} that CRM emits for un-selected amenities.
  const isSelected = (v) => {
    if (v === true) return true;
    if (v === false || v === null || v === undefined) return false;
    if (typeof v === 'object') {
      if ('value' in v) return String(v.value).toLowerCase() === 'yes' || v.value === true;
      if ('selected' in v) return Boolean(v.selected);
      return true; // dict with meta (variant/cost/price) implies selected
    }
    return Boolean(v);
  };
  const selectedNames = Object.entries(amenities)
    .filter(([, v]) => isSelected(v))
    .map(([k]) => k);
  const toggleName = (name) => {
    const next = { ...amenities };
    if (next[name]) {
      delete next[name];
    } else {
      next[name] = { value: 'Yes' };
    }
    onChange(roomIndex, 'amenities', next);
  };
  const setOption = (name, key, value) => {
    const next = { ...amenities };
    const meta = { ...(next[name] || { value: 'Yes' }), [key]: value };
    // Clearing Paid->Free should drop a previously-entered price
    if (key === 'cost' && value === 'Free') {
      delete meta.price;
    }
    next[name] = meta;
    onChange(roomIndex, 'amenities', next);
  };
  const label = room.room_type ? `Room ${roomIndex + 1}: ${room.room_type}${room.ac_type ? ` (${room.ac_type})` : ''}` : `Room ${roomIndex + 1}`;
  return (
    <div className="border border-gray-200 rounded-xl p-3 bg-gray-50/50" data-testid={`onboarding-room-amenities-${roomIndex}`}>
      <p className="text-xs font-semibold text-[#1B2A7B] mb-2">{label}</p>
      <CheckGrid
        title="Room Amenities"
        options={ROOM_AMENITIES}
        selected={selectedNames}
        onToggle={toggleName}
        testIdPrefix={`onboarding-room-${roomIndex}-amenities`}
        readOnly={readOnly}
        conditional={CONDITIONAL_AMENITIES}
        amenityOptions={amenities}
        onOptionChange={setOption}
      />
    </div>
  );
};

const OnboardingFormScreen = () => {
  const navigate = useNavigate();
  const { selectedProperty } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState([]);
  // iter113 — inline validation per field (blocks Next until mandatory fields are filled)
  const [fieldErrors, setFieldErrors] = useState({});

  const propertyId = selectedProperty?.id;
  const readOnly = form?.status === 'completed';

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const res = await onboardingAPI.get(propertyId);
      const r = res?.STATUS === 1 ? res.RESULT : {};
      setForm({
        property_name: r.property_name || '', property_type: r.property_type || '', star_rating: r.star_rating || '',
        total_rooms: r.total_rooms || '', property_description: r.property_description || '',
        owner_manager_name: r.owner_manager_name || '', official_email: r.official_email || '',
        mobile_number: r.mobile_number || '', alternative_contact: r.alternative_contact || '',
        full_address: r.full_address || '', state: r.state || '', city: r.city || '', pin_code: r.pin_code || '', google_map_link: r.google_map_link || '',
        room_inventory: (r.room_inventory && r.room_inventory.length) ? r.room_inventory.map(x => ({ ...emptyRoom(), ...x })) : [emptyRoom()],
        room_amenities: r.room_amenities || [], property_policies: r.property_policies || [],
        chargeable_facilities: r.chargeable_facilities || [], other_facilities: r.other_facilities || [],
        amenity_options: r.amenity_options || {}, additional_amenities: r.additional_amenities || '',
        account_holder_name: r.account_holder_name || '', bank_name: r.bank_name || '', account_number: r.account_number || '',
        ifsc_code: r.ifsc_code || '', branch_name: r.branch_name || '', upi_id: r.upi_id || '',
        cancelled_cheque: r.cancelled_cheque || '', payment_qr: r.payment_qr || '',
        status: r.status || 'draft',
      });
      if (r._prefilled_from_crm) {
        toast({ title: 'Pre-filled', description: 'Some fields are auto-filled. Review and complete the form.' });
      }
    } catch (e) {
      toast({ title: 'Failed to load', description: 'Could not load onboarding form', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [propertyId, toast]);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const toggleArr = (k, val) => setForm(prev => {
    const arr = prev[k] || [];
    const has = arr.includes(val);
    const next = { ...prev, [k]: has ? arr.filter(x => x !== val) : [...arr, val] };
    // Clear conditional options when a parent amenity is deselected
    if (has && prev.amenity_options && prev.amenity_options[val]) {
      const ao = { ...prev.amenity_options }; delete ao[val]; next.amenity_options = ao;
    }
    return next;
  });
  const setAmenityOption = (amenity, groupKey, value) => setForm(prev => {
    const ao = { ...(prev.amenity_options || {}) };
    ao[amenity] = { ...(ao[amenity] || {}), [groupKey]: value };
    return { ...prev, amenity_options: ao };
  });
  const setRoom = (i, k, v) => setForm(prev => {
    const rooms = [...prev.room_inventory]; rooms[i] = { ...rooms[i], [k]: v }; return { ...prev, room_inventory: rooms };
  });
  const addRoom = () => setForm(prev => ({ ...prev, room_inventory: [...prev.room_inventory, emptyRoom()] }));
  const removeRoom = (i) => setForm(prev => ({ ...prev, room_inventory: prev.room_inventory.filter((_, idx) => idx !== i) }));

  const buildPayload = () => ({
    ...form,
    star_rating: form.star_rating ? parseInt(form.star_rating) : null,
    total_rooms: form.total_rooms ? parseInt(form.total_rooms) : null,
    room_inventory: form.room_inventory
      .filter(r => r.room_type)
      .map(r => ({
        ...r,
        no_of_rooms: r.no_of_rooms ? parseInt(r.no_of_rooms) : null,
        room_size: r.room_size ? parseFloat(r.room_size) : null,
        base_price: r.base_price ? parseFloat(r.base_price) : null,
        weekend_price: r.weekend_price ? parseFloat(r.weekend_price) : null,
        extra_adult_price: r.extra_adult_price ? parseFloat(r.extra_adult_price) : null,
        extra_bed_price: r.extra_bed_price ? parseFloat(r.extra_bed_price) : null,
        no_of_adults: r.no_of_adults ? parseInt(r.no_of_adults) : null,
        no_of_children: r.no_of_children ? parseInt(r.no_of_children) : null,
      })),
  });

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      await onboardingAPI.save(propertyId, buildPayload());
      toast({ title: 'Draft saved', description: 'Your onboarding progress is saved.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?._friendlyMessage || 'Could not save draft', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // iter113 — Per-step mandatory-field validation. Returns {} if OK,
  // {fieldKey: 'Message'} otherwise. `room:i:key` = per-room error.
  const validateStep = (n) => {
    const e = {};
    const req = (k, label) => {
      const v = form?.[k];
      if (v === undefined || v === null || String(v).trim() === '') e[k] = `${label} is required`;
    };
    if (n === 1) {
      req('property_name', 'Property Name');
      req('property_type', 'Property Type');
      req('total_rooms', 'Total Number of Rooms');
      req('owner_manager_name', 'Owner / Manager Name');
      req('official_email', 'Official Email');
      req('mobile_number', 'Mobile Number');
      if (form?.official_email && !/^\S+@\S+\.\S+$/.test(form.official_email)) e.official_email = 'Enter a valid email';
      if (form?.mobile_number && !/^\d{10}$/.test(String(form.mobile_number).replace(/\D/g, ''))) e.mobile_number = 'Enter a 10-digit mobile number';
    }
    if (n === 2) {
      req('full_address', 'Full Address');
      req('state', 'State');
      req('city', 'City');
      req('pin_code', 'Pin Code');
      req('google_map_link', 'Google Map Link');
      if (form?.pin_code && !/^\d{6}$/.test(String(form.pin_code))) e.pin_code = 'Enter a 6-digit PIN code';
      if (form?.google_map_link && !/^https?:\/\//i.test(form.google_map_link)) e.google_map_link = 'Enter a full URL (https://...)';
    }
    if (n === 3) {
      (form?.room_inventory || []).forEach((r, i) => {
        if (!r.room_type) e[`room:${i}:room_type`] = 'Room Type is required';
        if (!r.no_of_rooms) e[`room:${i}:no_of_rooms`] = 'No. of rooms is required';
        if (!r.base_price) e[`room:${i}:base_price`] = 'Base Price is required';
        if (!r.no_of_adults) e[`room:${i}:no_of_adults`] = 'Max Adults is required';
        // extra_adult_price is required so extras never fall back to ₹0 silently
        if (r.extra_adult_price === '' || r.extra_adult_price === undefined || r.extra_adult_price === null) {
          e[`room:${i}:extra_adult_price`] = 'Extra Adult Price is required';
        }
      });
      if ((form?.room_inventory || []).length === 0) e.room_inventory = 'Add at least one room type';
    }
    if (n === 4) {
      // Amenities: Paid + price must be > 0 (already validated on submit; enforce here too)
      (form?.room_inventory || []).forEach((room, i) => {
        Object.entries(room.amenities || {}).forEach(([name, meta]) => {
          if (meta && meta.cost === 'Paid' && (!meta.price || meta.price <= 0)) {
            e[`room:${i}:amenity:${name}`] = `${name}: Paid price is required`;
          }
        });
      });
      ['property_policies', 'chargeable_facilities'].forEach((scope) => {
        (form?.[scope] || []).forEach((name) => {
          const meta = (form?.amenity_options || {})[name];
          if (meta && meta.cost === 'Paid' && (!meta.price || meta.price <= 0)) {
            e[`amenity:${name}`] = `${name}: Paid price is required`;
          }
        });
      });
    }
    if (n === 5) {
      req('account_holder_name', 'Account Holder Name');
      req('bank_name', 'Bank Name');
      req('account_number', 'Account Number');
      req('ifsc_code', 'IFSC Code');
      if (form?.ifsc_code && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(form.ifsc_code)) e.ifsc_code = 'Enter a valid IFSC (e.g. HDFC0000123)';
    }
    return e;
  };

  // iter85 — Auto-save on step navigation (Next/Previous only). Best-effort:
  // never block the navigation, never show a success toast (to avoid noise on
  // every click). On failure, surface a small destructive toast so the owner
  // knows the step's data didn't reach the server.
  const goToStep = async (nextStep) => {
    if (readOnly || nextStep === step) {
      setStep(nextStep);
      return;
    }
    // iter113 — Only validate when going FORWARD. Back button is always allowed.
    if (nextStep > step) {
      const errs = validateStep(step);
      if (Object.keys(errs).length) {
        setFieldErrors(errs);
        toast({
          title: 'Please complete required fields',
          description: `${Object.keys(errs).length} field(s) need attention`,
          variant: 'destructive',
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }
    setFieldErrors({});
    setSaving(true);
    try {
      await onboardingAPI.save(propertyId, buildPayload());
    } catch (e) {
      toast({
        title: 'Auto-save failed',
        description: e?._friendlyMessage || 'This step was not saved. Tap "Draft" to retry.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
      setStep(nextStep);
    }
  };

  const handleUpload = async (field, file) => {
    if (!file) return;
    try {
      // ensure a draft exists first so upload attaches to a row
      await onboardingAPI.save(propertyId, buildPayload());
      const res = await onboardingAPI.upload(propertyId, field, file);
      if (res?.STATUS === 1) {
        setField(field, res.RESULT.data_uri);
        toast({ title: 'Uploaded', description: `${field === 'payment_qr' ? 'Payment QR' : 'Cancelled cheque'} uploaded` });
      }
    } catch (e) {
      toast({ title: 'Upload failed', description: e?._friendlyMessage || 'Max 2MB image', variant: 'destructive' });
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setErrors([]);

    // iter84 — Paid + price validation. If any amenity/facility has cost="Paid",
    // price must be a positive integer. Block submission with a clear message.
    const paidErrors = [];
    (form.room_inventory || []).forEach((room, i) => {
      const label = room.room_type ? `Room ${i + 1} (${room.room_type})` : `Room ${i + 1}`;
      Object.entries(room.amenities || {}).forEach(([name, meta]) => {
        if (meta && meta.cost === 'Paid' && (!meta.price || meta.price <= 0)) {
          paidErrors.push(`${label} → ${name}: Paid price is required (₹ > 0)`);
        }
      });
    });
    ['property_policies', 'chargeable_facilities'].forEach((scope) => {
      (form[scope] || []).forEach((name) => {
        const meta = (form.amenity_options || {})[name];
        if (meta && meta.cost === 'Paid' && (!meta.price || meta.price <= 0)) {
          paidErrors.push(`${name}: Paid price is required (₹ > 0)`);
        }
      });
    });
    if (paidErrors.length) {
      setErrors(paidErrors);
      toast({ title: 'Enter Paid price', description: `${paidErrors.length} item(s) need a price`, variant: 'destructive' });
      setStep(4);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setSubmitting(false);
      return;
    }

    try {
      await onboardingAPI.save(propertyId, buildPayload());
      const res = await onboardingAPI.complete(propertyId);
      if (res?.STATUS === 1) {
        toast({ title: 'Onboarding Completed! 🎉', description: 'WhatsApp confirmation sent. Room types are now available in Walk-in Booking.' });
        setField('status', 'completed');
        navigate('/profile');
      } else {
        const errs = res?.RESULT?.errors || ['Please complete all required fields'];
        setErrors(errs);
        toast({ title: 'Incomplete form', description: `${errs.length} issue(s) to fix`, variant: 'destructive' });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (e) {
      toast({ title: 'Submit failed', description: e?._friendlyMessage || 'Try again', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !form) {
    return <div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 text-[#1B2A7B] animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      {/* Header */}
      <div className="bg-navy text-white p-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/profile')} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="onboarding-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Onboarding Form</h1>
            <p className="text-xs text-white/70">{selectedProperty?.property_name || selectedProperty?.name}</p>
          </div>
          {readOnly && (
            <span className="flex items-center gap-1 bg-[#1B2A7B] text-white text-xs font-semibold px-2.5 py-1 rounded-full" data-testid="onboarding-completed-badge">
              <CheckCircle2 className="w-3.5 h-3.5" /> Completed
            </span>
          )}
        </div>
        {/* Stepper */}
        <div className="flex items-center justify-between mt-4">
          {STEPS.map((s, idx) => {
            const Icon = s.icon;
            const done = step > s.id;
            const cur = step === s.id;
            return (
              <React.Fragment key={s.id}>
                <button onClick={() => goToStep(s.id)} disabled={saving} className="flex flex-col items-center gap-1 disabled:opacity-50" data-testid={`onboarding-step-${s.id}`}>
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs ${cur ? 'bg-[#1B2A7B] text-white' : done ? 'bg-[#1B2A7B]/40 text-white' : 'bg-white/15 text-white/60'}`}>
                    {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </span>
                  <span className={`text-[10px] ${cur ? 'text-white' : 'text-white/50'}`}>{s.label}</span>
                </button>
                {idx < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${done ? 'bg-[#1B2A7B]/50' : 'bg-white/15'}`} />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className="p-4 space-y-4 max-w-2xl mx-auto">
        {readOnly && (
          <div className="bg-[#EEF1FC] border border-[#1B2A7B]/30 rounded-lg p-3 flex items-center gap-2" data-testid="onboarding-readonly-banner">
            <Lock className="w-4 h-4 text-[#1B2A7B] flex-shrink-0" />
            <p className="text-sm text-[#1B2A7B]">This onboarding form is <span className="font-semibold">completed and verified</span>. Fields are view-only. Contact support to request changes.</p>
          </div>
        )}

        {errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid="onboarding-errors">
            <p className="text-sm font-semibold text-red-700 mb-1">Please fix:</p>
            <ul className="text-xs text-red-600 list-disc list-inside space-y-0.5">
              {errors.map((e, i) => (
                <li key={i}>
                  {typeof e === 'string' ? e : (e?.msg || e?.message || JSON.stringify(e))}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* STEP 1 — Property Details */}
        {step === 1 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3" data-testid="onboarding-step1">
            <h2 className="font-semibold text-gray-900">Property Details</h2>
            <Field label="Property Name" value={form.property_name} onChange={(v) => setField('property_name', v)} required testId="onboarding-property_name" readOnly={readOnly} error={fieldErrors.property_name} />
            <div>
              <label className={labelCls}>Property Type <span className="text-red-500">*</span></label>
              <select value={form.property_type} disabled={readOnly} onChange={(e) => setField('property_type', e.target.value)} className={inputCls} data-testid="onboarding-property_type">
                <option value="">Select type</option>
                {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Star Rating</label>
                <select value={form.star_rating} disabled={readOnly} onChange={(e) => setField('star_rating', e.target.value)} className={inputCls} data-testid="onboarding-star_rating">
                  <option value="">—</option>{[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n} Star</option>)}
                </select>
              </div>
              <Field label="Total Rooms" value={form.total_rooms} onChange={(v) => setField('total_rooms', v)} type="number" required testId="onboarding-total_rooms" readOnly={readOnly} error={fieldErrors.total_rooms} />
            </div>
            <div>
              <label className={labelCls}>Property Description</label>
              <textarea value={form.property_description} disabled={readOnly} onChange={(e) => setField('property_description', e.target.value)} rows={3} className={inputCls} data-testid="onboarding-property_description" />
            </div>
            <Field label="Owner / Manager Name" value={form.owner_manager_name} onChange={(v) => setField('owner_manager_name', v)} required testId="onboarding-owner_manager_name" readOnly={readOnly} error={fieldErrors.owner_manager_name} />
            <Field label="Official Email" value={form.official_email} onChange={(v) => setField('official_email', v)} type="email" required testId="onboarding-official_email" readOnly={readOnly} error={fieldErrors.official_email} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Mobile Number" value={form.mobile_number} onChange={(v) => setField('mobile_number', v)} required testId="onboarding-mobile_number" readOnly={readOnly} error={fieldErrors.mobile_number} />
              <Field label="Alternative Contact" value={form.alternative_contact} onChange={(v) => setField('alternative_contact', v)} testId="onboarding-alternative_contact" readOnly={readOnly} />
            </div>
          </div>
        )}

        {/* STEP 2 — Location */}
        {step === 2 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3" data-testid="onboarding-step2">
            <h2 className="font-semibold text-gray-900">Location Details</h2>
            <div>
              <label className={labelCls}>Full Address <span className="text-red-500">*</span></label>
              <textarea value={form.full_address} disabled={readOnly} onChange={(e) => setField('full_address', e.target.value)} rows={2} className={inputCls} data-testid="onboarding-full_address" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="State" value={form.state} onChange={(v) => setField('state', v)} required testId="onboarding-state" readOnly={readOnly} error={fieldErrors.state} />
              <Field label="City" value={form.city} onChange={(v) => setField('city', v)} required testId="onboarding-city" readOnly={readOnly} error={fieldErrors.city} />
            </div>
            <Field label="Pin Code" value={form.pin_code} onChange={(v) => setField('pin_code', v)} required testId="onboarding-pin_code" readOnly={readOnly} error={fieldErrors.pin_code} />
            <Field label="Google Map Link" value={form.google_map_link} onChange={(v) => setField('google_map_link', v)} required placeholder="https://maps.google.com/..." testId="onboarding-google_map_link" readOnly={readOnly} error={fieldErrors.google_map_link} />
            {/* iter112 — Clickable preview link, editable field stays above */}
            {form.google_map_link && (
              <a
                href={form.google_map_link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="onboarding-google-map-open"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#1B2A7B] hover:text-[#1B2A7B] hover:underline mt-1"
              >
                📍 View on Google Maps
              </a>
            )}
          </div>
        )}

        {/* STEP 3 — Room Inventory */}
        {step === 3 && (
          <div className="space-y-3" data-testid="onboarding-step3">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Room Inventory</h2>
              {!readOnly && (
                <button onClick={addRoom} className="flex items-center gap-1 text-[#1B2A7B] text-sm font-medium" data-testid="onboarding-add-room-category">
                  <Plus className="w-4 h-4" /> Add Category
                </button>
              )}
            </div>
            {form.room_inventory.map((room, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 space-y-3" data-testid={`onboarding-room-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500">Category {i + 1}</span>
                  {!readOnly && form.room_inventory.length > 1 && (
                    <button onClick={() => removeRoom(i)} className="text-red-400 hover:text-red-600" data-testid={`onboarding-remove-room-${i}`}><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Room Type</label>
                    <select value={room.room_type} disabled={readOnly} onChange={(e) => setRoom(i, 'room_type', e.target.value)} className={inputCls} data-testid={`onboarding-room-type-${i}`}>
                      <option value="">Select</option>
                      {ROOM_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                      {/* Preserve a synced CRM room type (e.g. "Standard Room") that
                          doesn't match the fixed list above — otherwise the <select>
                          falls back to the placeholder. */}
                      {room.room_type && !ROOM_TYPE_OPTIONS.includes(room.room_type) && (
                        <option key={`custom-${room.room_type}`} value={room.room_type}>{room.room_type}</option>
                      )}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>AC / Non-AC</label>
                    <select value={room.ac_type} disabled={readOnly} onChange={(e) => setRoom(i, 'ac_type', e.target.value)} className={inputCls} data-testid={`onboarding-room-ac-${i}`}>
                      <option value="">Select</option>{AC_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div><label className={labelCls}>No. of Rooms</label><input type="number" value={room.no_of_rooms} disabled={readOnly} onChange={(e) => setRoom(i, 'no_of_rooms', e.target.value)} className={inputCls} data-testid={`onboarding-room-count-${i}`} /></div>
                  <div><label className={labelCls}>Room Size</label><input type="number" value={room.room_size} disabled={readOnly} onChange={(e) => setRoom(i, 'room_size', e.target.value)} className={inputCls} /></div>
                  <div>
                    <label className={labelCls}>Unit</label>
                    <select value={room.size_unit} disabled={readOnly} onChange={(e) => setRoom(i, 'size_unit', e.target.value)} className={inputCls}>{SIZE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}</select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div><label className={labelCls}>Base Price/Night</label><input type="number" value={room.base_price} disabled={readOnly} onChange={(e) => setRoom(i, 'base_price', e.target.value)} className={inputCls} data-testid={`onboarding-room-price-${i}`} /></div>
                  <div><label className={labelCls}>Weekend Price</label><input type="number" value={room.weekend_price} disabled={readOnly} onChange={(e) => setRoom(i, 'weekend_price', e.target.value)} className={inputCls} /></div>
                  <div><label className={labelCls}>Extra Adult</label><input type="number" value={room.extra_adult_price} disabled={readOnly} onChange={(e) => setRoom(i, 'extra_adult_price', e.target.value)} className={inputCls} data-testid={`onboarding-room-extra-adult-${i}`} /></div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div><label className={labelCls}>Extra Bed Price</label><input type="number" value={room.extra_bed_price} disabled={readOnly} onChange={(e) => setRoom(i, 'extra_bed_price', e.target.value)} className={inputCls} data-testid={`onboarding-room-extra-bed-${i}`} placeholder="₹ per bed / night" /></div>
                  <div><label className={labelCls}>No. of Adults</label><input type="number" value={room.no_of_adults} disabled={readOnly} onChange={(e) => setRoom(i, 'no_of_adults', e.target.value)} className={inputCls} /></div>
                  <div><label className={labelCls}>No. of Children</label><input type="number" value={room.no_of_children} disabled={readOnly} onChange={(e) => setRoom(i, 'no_of_children', e.target.value)} className={inputCls} /></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* STEP 4 — Amenities & Policies */}
        {step === 4 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-5" data-testid="onboarding-step4">
            <h2 className="font-semibold text-gray-900">Amenities &amp; Policies</h2>

            {/* iter84: Room-Type-wise amenities — one block per room in inventory */}
            <div className="space-y-3" data-testid="onboarding-room-amenities-block">
              <p className="text-sm font-semibold text-gray-700">Room Amenities (per room type)</p>
              {(form.room_inventory || []).filter(r => r.room_type || r.no_of_rooms).length === 0 ? (
                <p className="text-xs text-gray-500 italic" data-testid="onboarding-no-rooms-warning">
                  Add at least one Room Type in Step 3 to configure room amenities.
                </p>
              ) : (
                (form.room_inventory || []).map((room, i) => (
                  (room.room_type || room.no_of_rooms) ? (
                    <RoomAmenitiesBlock
                      key={`room-amen-${i}`}
                      room={room}
                      roomIndex={i}
                      onChange={setRoom}
                      readOnly={readOnly}
                    />
                  ) : null
                ))
              )}
              {/* Backward-compat: legacy property-wide amenity selections, shown only
                  on completed/read-only forms where per-room data is absent. */}
              {readOnly && (form.room_amenities || []).length > 0 && (
                <div className="mt-2 p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-xs text-yellow-800" data-testid="onboarding-legacy-amenities-note">
                  Legacy property-wide amenities (from previous version):
                  <span className="ml-1 font-medium">{form.room_amenities.join(', ')}</span>
                </div>
              )}
            </div>

            <CheckGrid title="Property Policies" options={PROPERTY_POLICIES} selected={form.property_policies} onToggle={(o) => toggleArr('property_policies', o)} testIdPrefix="onboarding-property_policies" readOnly={readOnly} conditional={CONDITIONAL_AMENITIES} amenityOptions={form.amenity_options} onOptionChange={setAmenityOption} />
            <CheckGrid title="Chargeable Facilities" options={CHARGEABLE_FACILITIES} selected={form.chargeable_facilities} onToggle={(o) => toggleArr('chargeable_facilities', o)} testIdPrefix="onboarding-chargeable_facilities" readOnly={readOnly} conditional={CONDITIONAL_AMENITIES} amenityOptions={form.amenity_options} onOptionChange={setAmenityOption} />
            <CheckGrid title="Other Facilities" options={OTHER_FACILITIES} selected={form.other_facilities} onToggle={(o) => toggleArr('other_facilities', o)} testIdPrefix="onboarding-other_facilities" readOnly={readOnly} conditional={CONDITIONAL_AMENITIES} amenityOptions={form.amenity_options} onOptionChange={setAmenityOption} />
            <div>
              <label className={labelCls}>Additional Amenities</label>
              <textarea value={form.additional_amenities} disabled={readOnly} onChange={(e) => setField('additional_amenities', e.target.value)} rows={2} placeholder="Custom amenities (comma separated)" className={inputCls} data-testid="onboarding-additional_amenities" />
            </div>
          </div>
        )}

        {/* STEP 5 — Banking */}
        {step === 5 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3" data-testid="onboarding-step5">
            <h2 className="font-semibold text-gray-900">Banking Details</h2>
            <p className="text-xs text-gray-500">Provide complete bank details OR upload a cancelled cheque. Payment QR is mandatory.</p>
            <Field label="Account Holder Name" value={form.account_holder_name} onChange={(v) => setField('account_holder_name', v)} required testId="onboarding-account_holder_name" readOnly={readOnly} error={fieldErrors.account_holder_name} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Bank Name" value={form.bank_name} onChange={(v) => setField('bank_name', v)} required testId="onboarding-bank_name" readOnly={readOnly} error={fieldErrors.bank_name} />
              <Field label="Branch Name" value={form.branch_name} onChange={(v) => setField('branch_name', v)} testId="onboarding-branch_name" readOnly={readOnly} />
            </div>
            <Field label="Account Number" value={form.account_number} onChange={(v) => setField('account_number', v)} required testId="onboarding-account_number" readOnly={readOnly} error={fieldErrors.account_number} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="IFSC Code" value={form.ifsc_code} onChange={(v) => setField('ifsc_code', v)} required testId="onboarding-ifsc_code" readOnly={readOnly} error={fieldErrors.ifsc_code} />
              <Field label="UPI ID" value={form.upi_id} onChange={(v) => setField('upi_id', v)} testId="onboarding-upi_id" readOnly={readOnly} />
            </div>
            {/* Uploads */}
            <div className="grid grid-cols-2 gap-3 pt-1">
              <div>
                <label className={labelCls}>Cancelled Cheque</label>
                <label className={`flex flex-col items-center justify-center gap-1 p-3 border border-dashed border-gray-300 rounded-lg ${readOnly ? 'cursor-default' : 'cursor-pointer hover:border-[#1B2A7B]'}`} data-testid="onboarding-upload-cheque">
                  {form.cancelled_cheque ? <img src={form.cancelled_cheque} alt="cheque" className="h-16 object-contain" /> : <><Upload className="w-5 h-5 text-gray-400" /><span className="text-xs text-gray-400">{readOnly ? 'Not provided' : 'Upload'}</span></>}
                  {!readOnly && <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload('cancelled_cheque', e.target.files[0])} />}
                </label>
              </div>
              <div>
                <label className={labelCls}>Payment QR <span className="text-red-500">*</span></label>
                <label className={`flex flex-col items-center justify-center gap-1 p-3 border border-dashed border-gray-300 rounded-lg ${readOnly ? 'cursor-default' : 'cursor-pointer hover:border-[#1B2A7B]'}`} data-testid="onboarding-upload-qr">
                  {form.payment_qr ? <img src={form.payment_qr} alt="qr" className="h-16 object-contain" /> : <><Upload className="w-5 h-5 text-gray-400" /><span className="text-xs text-gray-400">{readOnly ? 'Not provided' : 'Upload QR'}</span></>}
                  {!readOnly && <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload('payment_qr', e.target.files[0])} />}
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 flex items-center gap-2 max-w-2xl mx-auto">
        {step > 1 && (
          <button onClick={() => goToStep(step - 1)} disabled={saving} className="px-4 py-2.5 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium disabled:opacity-50" data-testid="onboarding-prev-btn">Back</button>
        )}
        {!readOnly && (
          <button onClick={handleSaveDraft} disabled={saving} className="flex items-center gap-1 px-4 py-2.5 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium disabled:opacity-50" data-testid="onboarding-save-draft-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Draft
          </button>
        )}
        {readOnly ? (
          <button onClick={() => navigate('/profile')} className="flex-1 flex items-center justify-center gap-1 py-2.5 rounded-lg bg-[#1B2A7B] text-white text-sm font-semibold" data-testid="onboarding-done-btn">
            <CheckCircle2 className="w-4 h-4" /> Done
          </button>
        ) : step < 5 ? (
          <button onClick={() => goToStep(step + 1)} disabled={saving} className="flex-1 flex items-center justify-center gap-1 py-2.5 rounded-lg bg-[#1B2A7B] text-white text-sm font-semibold disabled:opacity-50" data-testid="onboarding-next-btn">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Next
          </button>
        ) : (
          <button onClick={handleSubmit} disabled={submitting} className="flex-1 flex items-center justify-center gap-1 py-2.5 rounded-lg bg-[#1B2A7B] text-white text-sm font-semibold disabled:opacity-50" data-testid="onboarding-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Submit Onboarding
          </button>
        )}
      </div>
    </div>
  );
};

export default OnboardingFormScreen;
